import { getCentroidCoordinates } from "@phc/common"
import {
  BranchType,
  EventStatus,
  OperationalStatus,
  PlatformType,
  SensorFunction,
  SensorType,
  UnitType,
} from "@phc/common/gen/threatAnalyticsGen"
import { isAxiosError } from "axios"
import { forward, toPoint } from "mgrs"
import { extraColors } from "../../utils/theme"
import type { DropdownOption } from "../Shared/Dropdown"

export function parseCentroid(wkt?: string | null) {
  return getCentroidCoordinates(wkt)
}

/** Wrap a longitude into the [-180, 180] range. */
function normalizeLng(lng: number): number {
  return ((((lng + 180) % 360) + 360) % 360) - 180
}

export function toCentroid(lat: number, lng: number) {
  return `POINT (${normalizeLng(lng)} ${lat})`
}

export function mgrsToLatLng(mgrsStr: string): { lat: number; lng: number } {
  const [lng, lat] = toPoint(mgrsStr)
  return { lat, lng }
}

export function latLngToMgrs(lat: number, lng: number): string {
  return forward([normalizeLng(lng), lat])
}

// Grid zone designator (1–60) + band (C–X, no I/O) + 2 grid-square letters (no I/O) + optional even-digit easting/northing
const MGRS_REGEX =
  /^([1-9]|[1-5][0-9]|60)[C-HJ-NP-X][A-HJ-NP-Z]{2}(\d{2}|\d{4}|\d{6}|\d{8}|\d{10})?$/i

/**
 * Validates an MGRS string. Returns an error message string if invalid, or null if valid.
 */
export function validateMgrs(mgrsStr: string): string | null {
  if (!MGRS_REGEX.test(mgrsStr)) {
    return "Invalid MGRS format (e.g. 33UUP0500)"
  }
  try {
    toPoint(mgrsStr)
  } catch {
    return "MGRS coordinate out of valid range"
  }
  return null
}

/** Explicit display labels for each enum value — avoids brittle string manipulation. */
export const nodeTypeLabels: Record<PlatformType, string> = {
  [PlatformType.PLATFORM_TYPE_UNSPECIFIED]: "Unspecified",
  [PlatformType.PLATFORM_TYPE_FACILITY]: "Facility",
  [PlatformType.PLATFORM_TYPE_EXPEDITIONARY]: "Expeditionary",
  [PlatformType.PLATFORM_TYPE_VESSEL]: "Vessel",
  [PlatformType.PLATFORM_TYPE_AIRCRAFT]: "Aircraft",
  [PlatformType.PLATFORM_TYPE_UNMANNED_SYSTEM]: "Unmanned System",
}

export const operationalStatusLabels: Record<OperationalStatus, string> = {
  [OperationalStatus.OPERATIONAL_STATUS_UNSPECIFIED]: "Unspecified",
  [OperationalStatus.OPERATIONAL_STATUS_FMC]: "FMC",
  [OperationalStatus.OPERATIONAL_STATUS_PMC]: "PMC",
  [OperationalStatus.OPERATIONAL_STATUS_NMC]: "NMC",
}

export const unitTypeLabels: Record<UnitType, string> = {
  [UnitType.UNIT_TYPE_UNSPECIFIED]: "Unspecified",
  [UnitType.UNIT_TYPE_MILITARY]: "Military",
  [UnitType.UNIT_TYPE_CIVILIAN]: "Civilian",
  [UnitType.UNIT_TYPE_NGO]: "NGO",
}

export const branchTypeLabels: Record<BranchType, string> = {
  [BranchType.BRANCH_TYPE_UNSPECIFIED]: "Unspecified",
  [BranchType.BRANCH_TYPE_ARMY]: "Army",
  [BranchType.BRANCH_TYPE_NAVY]: "Navy",
  [BranchType.BRANCH_TYPE_MARINES]: "Marines",
  [BranchType.BRANCH_TYPE_AIR_FORCE]: "Air Force",
  [BranchType.BRANCH_TYPE_SPACE_FORCE]: "Space Force",
}

export const sensorTypeLabels: Record<SensorType, string> = {
  [SensorType.SENSOR_TYPE_UNSPECIFIED]: "Unspecified",
  [SensorType.SENSOR_TYPE_WEATHER_STATION]: "Weather Station",
  [SensorType.SENSOR_TYPE_CAMERA]: "Camera",
  [SensorType.SENSOR_TYPE_AIR_COLLECTION]: "Air Collection",
  [SensorType.SENSOR_TYPE_WATER_COLLECTION]: "Water Collection",
  [SensorType.SENSOR_TYPE_SURFACE_COLLECTION]: "Surface Collection",
  [SensorType.SENSOR_TYPE_LABORATORY]: "Laboratory",
  [SensorType.SENSOR_TYPE_WEARABLE]: "Wearable",
  [SensorType.SENSOR_TYPE_UNMANNED_SYSTEMS]: "Unmanned Systems",
}

export const sensorFunctionLabels: Record<SensorFunction, string> = {
  [SensorFunction.SENSOR_FUNCTION_UNSPECIFIED]: "Unspecified",
  [SensorFunction.SENSOR_FUNCTION_COLLECTOR_DETECTOR]: "Collector / Detector",
  [SensorFunction.SENSOR_FUNCTION_DIAGNOSTIC_TOOL]: "Diagnostic Tool",
  [SensorFunction.SENSOR_FUNCTION_VECTOR_DIAGNOSTIC_TOOL]:
    "Vector Diagnostic Tool",
  [SensorFunction.SENSOR_FUNCTION_CLASSIFIER_SURVEILLANCE]:
    "Classifier / Surveillance",
}

export const eventStatusLabels: Record<EventStatus, string> = {
  [EventStatus.EVENT_STATUS_INITIAL]: "Initial",
  [EventStatus.EVENT_STATUS_CONFIRMED]: "Confirmed",
  [EventStatus.EVENT_STATUS_ARCHIVED]: "Archived",
}

function optionsFromLabels<T extends string>(
  labels: Record<T, string>
): DropdownOption[] {
  return Object.entries<string>(labels)
    .filter(([key]) => !key.endsWith("UNSPECIFIED"))
    .map(([value, name]) => ({ value, name }))
}

export const nodeTypeOptions = optionsFromLabels(nodeTypeLabels)

export const operationalStatusOptions = optionsFromLabels(
  operationalStatusLabels
)

export const sensorTypeOptions = optionsFromLabels(sensorTypeLabels)

export const sensorFunctionOptions = optionsFromLabels(sensorFunctionLabels)

export const unitTypeOptions = optionsFromLabels(unitTypeLabels)

export const branchTypeOptions = optionsFromLabels(branchTypeLabels)

export const eventStatusOptions = optionsFromLabels(eventStatusLabels)

export interface StatusChipColor {
  bg: string
  border: string
  fg: string
}

const defaultChipColor: StatusChipColor = {
  bg: extraColors.dark10,
  border: extraColors.dark20,
  fg: extraColors.medium,
}

export const operationalStatusChipColors: Record<
  OperationalStatus,
  StatusChipColor
> = {
  [OperationalStatus.OPERATIONAL_STATUS_FMC]: {
    bg: extraColors.status.greenDeep,
    border: extraColors.status.greenMuted,
    fg: extraColors.status.greenLight,
  },
  [OperationalStatus.OPERATIONAL_STATUS_PMC]: {
    bg: extraColors.status.yellowDeep,
    border: extraColors.status.yellowMuted,
    fg: extraColors.status.yellowLight,
  },
  [OperationalStatus.OPERATIONAL_STATUS_NMC]: {
    bg: extraColors.status.redDeep,
    border: extraColors.status.redMuted,
    fg: extraColors.status.redLight,
  },
  [OperationalStatus.OPERATIONAL_STATUS_UNSPECIFIED]: defaultChipColor,
}

export const eventStatusChipColors: Record<EventStatus, StatusChipColor> = {
  [EventStatus.EVENT_STATUS_INITIAL]: {
    bg: extraColors.status.navyDeep,
    border: extraColors.status.navyMuted,
    fg: extraColors.status.navyLight,
  },
  [EventStatus.EVENT_STATUS_CONFIRMED]: {
    bg: extraColors.status.greenDeep,
    border: extraColors.status.greenMuted,
    fg: extraColors.status.greenLight,
  },
  [EventStatus.EVENT_STATUS_ARCHIVED]: defaultChipColor,
}

/** Type guards for generated enum constants — use instead of `as` casts. */
export function isNodeType(v: string): v is PlatformType {
  return (Object.values(PlatformType) as string[]).includes(v)
}
export function isOperationalStatus(v: string): v is OperationalStatus {
  return (Object.values(OperationalStatus) as string[]).includes(v)
}
export function isSensorType(v: string): v is SensorType {
  return (Object.values(SensorType) as string[]).includes(v)
}
export function isSensorFunction(v: string): v is SensorFunction {
  return (Object.values(SensorFunction) as string[]).includes(v)
}
export function isUnitType(v: string): v is UnitType {
  return (Object.values(UnitType) as string[]).includes(v)
}
export function isBranchType(v: string): v is BranchType {
  return (Object.values(BranchType) as string[]).includes(v)
}
export function isEventStatus(v: string): v is EventStatus {
  return (Object.values(EventStatus) as string[]).includes(v)
}

export const CREATE_NEW_SENTINEL = "__CREATE_NEW__"

/** Extract a human-readable message from an API error (e.g. 422 validation). */
export function extractApiError(error: unknown, fallback: string): string {
  if (isAxiosError(error)) {
    const data: unknown = error.response?.data
    if (Array.isArray(data) && data.length > 0) {
      const msgs = data.map((e: { msg?: string }) => e.msg).filter(Boolean)
      if (msgs.length) return msgs.join("; ")
    }
    if (data && typeof data === "object" && "detail" in data) {
      const detail = (data as { detail: unknown }).detail
      if (typeof detail === "string") return detail
      if (Array.isArray(detail) && detail.length > 0) {
        const msgs = detail.map((e: { msg?: string }) => e.msg).filter(Boolean)
        if (msgs.length) return msgs.join("; ")
      }
    }
  }
  return fallback
}
